import { type ElementType, type ReactNode, type MouseEvent, useCallback } from 'react';
import { isEmpty } from 'lodash/fp';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import type { PolymorphicProps } from 'app/globalTypes';
import { Defaults, Theme } from 'app/lib/constants';
import { useTheme } from 'app/components/core/ThemeProvider';
import { Icon, IconSize, type IconVariant } from 'app/components/core/Icon';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import * as styles from './Button.css';

const cx = classnames.bind(styles);

export enum ButtonSize {
    EXTRA_SMALL = 'extra-small',
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large',
    AUTO = 'auto'
}

export enum ButtonVariant {
    SPECIAL = 'special',
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    SECONDARY_BLUE = 'secondary-blue',
    TERTIARY = 'tertiary',
    HELP = 'help',
    GHOST = 'ghost'
}

export enum ButtonEdge {
    ROUNDED = 'rounded',
    FLAT = 'flat',
    NONE = 'none'
}

const SIZE_CLASSES = {
    [ButtonSize.EXTRA_SMALL]: 'size-xs',
    [ButtonSize.SMALL]: 'size-sm',
    [ButtonSize.MEDIUM]: 'size-md',
    [ButtonSize.LARGE]: 'size-lg',
    [ButtonSize.EXTRA_LARGE]: 'size-xl',
    [ButtonSize.AUTO]: 'size-auto'
} as const;

const VARIANT_CLASSES = {
    [ButtonVariant.SPECIAL]: 'variant-special',
    [ButtonVariant.PRIMARY]: 'variant-primary',
    [ButtonVariant.SECONDARY]: 'variant-secondary',
    [ButtonVariant.SECONDARY_BLUE]: 'variant-secondary-blue',
    [ButtonVariant.TERTIARY]: 'variant-tertiary',
    [ButtonVariant.HELP]: 'variant-help',
    [ButtonVariant.GHOST]: 'variant-ghost'
} as const;

const EDGE_CLASSES = {
    [ButtonEdge.ROUNDED]: 'edge-rounded',
    [ButtonEdge.FLAT]: 'edge-flat',
    [ButtonEdge.NONE]: 'edge-none'
} as const;

const LOADING_SIZE = {
    [ButtonSize.EXTRA_SMALL]: LoadingSize.EXTRA_SMALL,
    [ButtonSize.SMALL]: LoadingSize.SMALL,
    [ButtonSize.MEDIUM]: LoadingSize.SMALL,
    [ButtonSize.LARGE]: LoadingSize.MEDIUM,
    [ButtonSize.EXTRA_LARGE]: LoadingSize.MEDIUM,
    [ButtonSize.AUTO]: LoadingSize.MEDIUM
} as const;

export interface Props {
    testId?: string;
    className?: string;
    children?: ReactNode;
    type?: 'button' | 'submit';
    icon?: IconVariant;
    iconEnd?: IconVariant;
    iconSize?: IconSize;
    onClick?: (event: MouseEvent) => void;
    onMouseDown?: (event: MouseEvent) => void;
    disabled?: boolean;
    label?: string;
    fullWidth?: boolean;
    inline?: boolean;
    size?: ButtonSize;
    variant?: ButtonVariant;
    edge?: ButtonEdge;
    theme?: Theme;
    isProcessing?: boolean;
    processingMessage?: string;
}

export function Button<As extends ElementType>(props: PolymorphicProps<As, Props>) {
    const {
        as: Component = 'button',
        testId,
        className,
        type = 'button',
        children = null,
        icon,
        iconEnd,
        iconSize = IconSize.EXTRA_SMALL,
        onClick = Defaults.NOOP,
        disabled = false,
        label,
        fullWidth = false,
        inline = false,
        size = ButtonSize.LARGE,
        edge = ButtonEdge.ROUNDED,
        variant = ButtonVariant.SECONDARY,
        theme: themeDefault = Theme.LIGHT,
        isProcessing = false,
        processingMessage: suppliedProcessingMessage,
        ...rest
    } = props;

    const { t } = useTranslation();
    const { themeClassname } = useTheme(themeDefault);
    const processingMessage = suppliedProcessingMessage ?? t('common:processing');

    const modifiers = {
        'full-width': fullWidth,
        'icon-only': isEmpty(children),
        'inline': inline
    };

    const classes = cx(
        'base',
        themeClassname,
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        EDGE_CLASSES[edge],
        modifiers,
        className
    );

    const handleClick = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            onClick(event);
        },
        [onClick]
    );

    const renderIcon = (variant: IconVariant) => (
        <Icon className={styles.icon} variant={variant} size={iconSize} />
    );

    return (
        <Component
            data-testid={testId}
            className={classes}
            type={type}
            aria-live="polite"
            aria-busy={isProcessing}
            aria-label={isProcessing ? processingMessage : label}
            aria-disabled={disabled || isProcessing}
            onClick={disabled || isProcessing ? undefined : handleClick}
            title={label}
            {...rest}>
            {icon && renderIcon(icon)}
            {children}
            {iconEnd && renderIcon(iconEnd)}
            {isProcessing && (
                <span className={styles.loading}>
                    <Loading size={LOADING_SIZE[size]} testId="processing-button" />
                </span>
            )}
        </Component>
    );
}
